import React, { useEffect, useState } from 'react'
import withTranslation from 'Common/withTranslation';
import { WithTranslationProps } from 'Common/Components/Translation/WithTranslationProps';
import { ShippingAddressEditorProps } from './ShippingAddressEditorProps';
import Subtitle from 'Common/Components/Titles/Subtitle';
import { X } from 'lucide-react';
import LoadingButton from 'Common/Components/LoadingOverlay/LoadingButton/LoadingButton';
import withMessages, { WithMessagesProps } from 'Common/withMessages';
import withNotifications, { WithNotificationsProps } from 'Common/withNotifications';
import { MessageType } from 'Common/Components/Messages/messageTypes';
import { NotificationType } from 'Common/Components/Notifications/notificationTypes';
import { axios_helper_async } from 'helpers/Axios';
import { ShippingAddressEditorViewModel } from 'Common/ViewModels/UserProfile/AddressesEditor/ShippingAddressEditorViewModel';
import { WithAuthProps } from 'Common/Components/Authentication/WithAuthProps';
import withAuth from 'Common/Components/Authentication/withAuth';
import { ShippingAddressEditorErrorsViewModel } from 'Common/ViewModels/Errors/UserProfile/AddressEditor/ShippingAddressEditorErrorsViewModel';
import Select from 'react-select';
import { Options } from 'Common/Models/Options';
import { countriesTranslations } from 'Common/constants/countriesLocales';

const ShippingAddressEditor: React.FC<WithTranslationProps & ShippingAddressEditorProps & WithMessagesProps & WithNotificationsProps & WithAuthProps> = ({
    address,
    isOpen,
    onClose,
    close,
    t,
    i18n,
    messages,
    notifications,
    account
}) => {
    const modelFields: Record<keyof ShippingAddressEditorViewModel, string> = {
        // id: 'id',
        addressLine: 'addressLine',
        city: 'city',
        company: 'company',
        country: 'country',
        customerId: 'customerId',
        phone: 'phone',
        state: 'state',
        street: 'street',
        zipCode: 'zipCode',
        // country: 'country'
    }
    const initialModel: ShippingAddressEditorViewModel = {
        // id: address.id,
        addressLine: address?.addressLine || '',
        city: address?.city || '',
        company: address?.company || '',
        country: address?.country || '',
        customerId: address?.customerId || Number(account.id),
        phone: address?.phone || '',
        state: address?.state || '' ,
        street: address?.street || '',
        zipCode: address?.zipCode || '',
        // country: address.country
    }
    const [model, setModel] = useState<ShippingAddressEditorViewModel>(initialModel);
    const [errors, setErrors] = useState<ShippingAddressEditorErrorsViewModel>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [countries, setCountries] = useState<Options[]>([]);

    const handleSave = async () => {
        try {
            setIsLoading(true);
            if (validModel()) {
                if (model.customerId === 0) {
                    if (account.id && account.id != '0') {
                        model.customerId = Number(account.id)
                    }
                    else {
                        throw "UserProfile.Errors.GetCustomerShippingAddress";
                    }
                }
                const response = await axios_helper_async<boolean>("/Account/Update-Customer-Shipping-Address", 'PATCH', model);

                messages.setMessages(response.messages);
                notifications.setNotifications(response.notifications);

                if (response.isSuccess) {
                    if (response.data) {
                        notifications.showNotification("UserProfile.ShippingAddressEditor.Successes.Save", NotificationType.Success);
                        close();
                    }
                    else {
                        messages.showMessage("", MessageType.Error, "UserProfile.ShippingAddressEditor.Errors.Save");
                    }
                }
                else {
                    messages.showMessage("UserProfile.Errors.GetCustomerShippingAddress", MessageType.Error, "Error");
                }
            }
        }
        catch (err: any) {
            notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
        }
        finally {
            setIsLoading(false);
        }
    }

    const validModel = () => {
        let newErrors: ShippingAddressEditorErrorsViewModel = {};

        if (!model.street) newErrors.street = "UserProfile.ShippingAddressEditor.Errors.Street";
        if (!model.addressLine) newErrors.addressLine = "UserProfile.ShippingAddressEditor.Errors.AddressLine";
        if (!model.city) newErrors.city = "UserProfile.ShippingAddressEditor.Errors.City";
        if (!model.state) newErrors.state = "UserProfile.ShippingAddressEditor.Errors.State";
        if (!model.zipCode) newErrors.zipCode = "UserProfile.ShippingAddressEditor.Errors.ZipCode";
        if (!model.country || !model.country) newErrors.country = "UserProfile.ShippingAddressEditor.Errors.Country";
        if (!model.company) newErrors.company = "UserProfile.ShippingAddressEditor.Errors.Company";
        if (!model.phone) newErrors.phone = "UserProfile.ShippingAddressEditor.Errors.Phone";

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    }

    const handleSelectChange = (selectedOption: any, actionMeta: any) => {
        setModel((prevModel) => ({
            ...prevModel,
            country: selectedOption ? selectedOption.value : null,
        }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setModel(prevModel => ({
            ...prevModel,
            [name]: value
        }));
    }

    const handleChangeCountries = () => {
        let newCountries = countriesTranslations.find(translation => translation.language === i18n.language)?.values;
        if (!newCountries) {
            newCountries = countriesTranslations[0].values;
        }

        setCountries(newCountries);
    }

    useEffect(() => {
        if (address) {
            setModel(address);
        }
    }, [address])

    useEffect(() => {

        handleChangeCountries();

        i18n.on("languageChanged", handleChangeCountries);

        return () => {
            i18n.off("languageChanged", handleChangeCountries);
        }
    }, [])

    return (
        <React.Fragment>
            {
                isOpen ?
                    <div className="flex justify-center items-center overflow-hidden fixed inset-0 z-[1005] p-2 min-h-screen text-xs">
                        <div className="my-8 mx-auto max-w-sm sm:max-w-md md:max-w-lg z-[47] bg-white rounded-lg shadow-lg overflow-hidden w-full">
                            <div className="flex flex-col items-center p-6 text-center w-full gap-2">
                                <div className="flex flex-row justify-center items-center w-full">
                                    <div className="flex justify-center items-center w-full">
                                        {/* <h3 className="text-md font-bold text-black w-full">{t("Product.ProductRequest.Title")}</h3> */}
                                        <Subtitle text={t("UserProfile.ShippingAddressEditor.Title")}></Subtitle>
                                    </div>
                                    {!isLoading ?
                                        <div className="flex justify-center items-center">
                                            <button onClick={close}><X></X></button>
                                        </div>
                                        :
                                        null
                                    }
                                </div>
                                <div className="flex flex-col justify-center items-center w-full gap-2">
                                    <label className="form-label">{t("UserProfile.ShippingAddressEditor.Street")}<span className="text-red-500">*</span></label>
                                    <input
                                        className={`form-input ${errors?.street ? "border-red-500" : null}`}
                                        id={modelFields.street}
                                        name={modelFields.street}
                                        value={model.street}
                                        onChange={handleChange}
                                        placeholder={t("UserProfile.ShippingAddressEditor.Street")}
                                    />
                                    {errors && errors.street && <div className="mt-1 text-sm text-red-500">{t(errors.street)}</div>}
                                </div>
                                <div className="flex flex-col justify-center items-center w-full gap-2">
                                    <label className="form-label">{t("UserProfile.ShippingAddressEditor.AddressLine")}<span className="text-red-500">*</span></label>
                                    <input
                                        className={`form-input ${errors?.addressLine ? "border-red-500" : null}`}
                                        id={modelFields.addressLine}
                                        name={modelFields.addressLine}
                                        value={model.addressLine}
                                        onChange={handleChange}
                                        placeholder={t("UserProfile.ShippingAddressEditor.AddressLine")}
                                    />
                                    {errors && errors.addressLine && <div className="mt-1 text-sm text-red-500">{t(errors.addressLine)}</div>}
                                </div>
                                <div className="flex flex-col justify-center items-center w-full gap-2">
                                    <label className="form-label">{t("UserProfile.ShippingAddressEditor.City")}<span className="text-red-500">*</span></label>
                                    <input
                                        className={`form-input ${errors?.city ? "border-red-500" : null}`}
                                        id={modelFields.city}
                                        name={modelFields.city}
                                        value={model.city}
                                        onChange={handleChange}
                                        placeholder={t("UserProfile.ShippingAddressEditor.City")}
                                    />
                                    {errors && errors.city && <div className="mt-1 text-sm text-red-500">{t(errors.city)}</div>}
                                </div>
                                <div className="flex flex-col justify-center items-center w-full gap-2">
                                    <label className="form-label">{t("UserProfile.ShippingAddressEditor.State")}<span className="text-red-500">*</span></label>
                                    <input
                                        className={`form-input ${errors?.state ? "border-red-500" : null}`}
                                        id={modelFields.state}
                                        name={modelFields.state}
                                        value={model.state}
                                        onChange={handleChange}
                                        placeholder={t("UserProfile.ShippingAddressEditor.State")}
                                    />
                                    {errors && errors.state && <div className="mt-1 text-sm text-red-500">{t(errors.state)}</div>}
                                </div>
                                <div className="flex flex-col justify-center items-center w-full gap-2">
                                    <label className="form-label">{t("UserProfile.ShippingAddressEditor.ZipCode")}<span className="text-red-500">*</span></label>
                                    <input
                                        className={`form-input ${errors?.zipCode ? "border-red-500" : null}`}
                                        id={modelFields.zipCode}
                                        name={modelFields.zipCode}
                                        value={model.zipCode}
                                        onChange={handleChange}
                                        placeholder={t("UserProfile.ShippingAddressEditor.ZipCode")}
                                    />
                                    {errors && errors.zipCode && <div className="mt-1 text-sm text-red-500">{t(errors.zipCode)}</div>}
                                </div>
                                <div className="flex flex-col justify-center items-center w-full gap-2">
                                    <label className="form-label">
                                        {t("UserProfile.BillingAddressEditor.Country")}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                        className={`w-full ${errors.country ? "border-red-500" : "border-slate-200"}`}
                                        name={modelFields.country}
                                        value={countries.find(country => country.value === model.country)}
                                        options={countries}
                                        placeholder={t("UserProfile.BillingAddressEditor.Country")}
                                        onChange={handleSelectChange}
                                    />
                                    {errors && errors.country && (
                                        <div className="mt-1 text-sm text-red-500">
                                            {t(errors.country)}
                                        </div>
                                    )}
                                </div>
                                {/* <div className="flex flex-col justify-center items-center w-full gap-2">
                                    <label className="form-label">{t("UserProfile.ShippingAddressEditor.Country")}<span className="text-red-500">*</span></label>
                                    <input
                                        className={`form-input ${errors?.country ? "border-red-500" : null}`}
                                        id={modelFields.country}
                                        name={modelFields.country}
                                        value={model.country}
                                        onChange={handleChange}
                                        placeholder={t("UserProfile.ShippingAddressEditor.Country")}                                        
                                    />
                                    {errors && errors.country && <div className="mt-1 text-sm text-red-500">{t(errors.country)}</div>}
                                </div> */}
                                <div className="flex flex-col justify-center items-center w-full gap-2">
                                    <label className="form-label">{t("UserProfile.ShippingAddressEditor.Company")}<span className="text-red-500">*</span></label>
                                    <input
                                        className={`form-input ${errors?.company ? "border-red-500" : null}`}
                                        id={modelFields.company}
                                        name={modelFields.company}
                                        value={model.company}
                                        onChange={handleChange}
                                        placeholder={t("UserProfile.ShippingAddressEditor.Company")}
                                    />
                                    {errors && errors.company && <div className="mt-1 text-sm text-red-500">{t(errors.company)}</div>}
                                </div>
                                <div className="flex flex-col justify-center items-center w-full gap-2">
                                    <label className="form-label">{t("UserProfile.ShippingAddressEditor.Phone")}<span className="text-red-500">*</span></label>
                                    <input
                                        className={`form-input ${errors?.phone ? "border-red-500" : null}`}
                                        id={modelFields.phone}
                                        name={modelFields.phone}
                                        value={model.phone}
                                        onChange={handleChange}
                                        placeholder={t("UserProfile.ShippingAddressEditor.Phone")}
                                    />
                                    {errors && errors.phone && <div className="mt-1 text-sm text-red-500">{t(errors.phone)}</div>}
                                </div>
                                {
                                    isLoading ?
                                        <LoadingButton />
                                        :
                                        <button className="btn btn-custom flex flex-row justify-center items-center gap-2 w-full" onClick={handleSave}>{t("UserProfile.ShippingAddressEditor.Save")}</button>
                                }
                            </div>
                        </div>
                        <div className="opacity-50 fixed inset-0 z-[46] bg-black"></div>
                    </div>
                    :
                    null
            }
        </React.Fragment>
    );
}

export default withTranslation(withMessages(withNotifications(withAuth(ShippingAddressEditor))));